import { Keys } from "./keys";

export default class AccessibleTabWidget {
  private readonly _tabList: HTMLElement;
  private readonly _tabs: HTMLElement[];
  private _selectedTab: HTMLElement | undefined;

  public constructor(tabList: HTMLElement) {
    this._tabList = tabList;
    // Don't handle this here if the tabs are using JQuery Bootstrap
    if (this._tabList.querySelector("[data-toggle='tab']")) {
      return;
    }
    this._tabs = Array.from(tabList.querySelectorAll("[role='tab']"));
    this._selectedTab = tabList.querySelector("[aria-selected='true']");
    if (this._tabs.length > 0) {
      this.initialize();
    }
  }

  private readonly initialize = () => {
    this._tabs.forEach((tab: HTMLElement, index: number) => {
      if (index === 0 && !this._selectedTab) {
        this.selectTab(tab);
      } else if (tab !== this._selectedTab) {
        this.deselectTab(tab);
      }

      tab.addEventListener("click", this.onTabClick);
      tab.addEventListener("keydown", this.onTabKeydown);
    });
  };

  private readonly onTabClick = (event: Event) => {
    const clickedTab = event.currentTarget as HTMLElement;
    this.deselectTab(this._selectedTab);
    this.selectTab(clickedTab);

    const tabPanel = this.getTabPanel(clickedTab);
    tabPanel?.focus();
  };

  private readonly selectTab = (tab: HTMLElement) => {
    this._selectedTab = tab;
    this.setActiveDescendent(tab.id);
    tab.setAttribute("tabindex", "0");
    tab.setAttribute("aria-selected", "true");
    tab.parentElement.classList.add("active");

    const tabPanel = this.getTabPanel(tab);
    tabPanel?.removeAttribute("aria-hidden");
    tabPanel?.removeAttribute("hidden");
    tabPanel?.classList.add("active");
  };

  private readonly deselectTab = (tab: HTMLElement | undefined) => {
    if (!tab) {
      return;
    }
    tab.setAttribute("tabindex", "-1");
    tab.setAttribute("aria-selected", "false");
    tab.parentElement.classList.remove("active");

    const tabPanel = this.getTabPanel(tab);
    tabPanel?.setAttribute("hidden", "hidden");
    tabPanel?.classList.remove("active");
  };

  private readonly setActiveDescendent = (tabId: string) => {
    this._tabList.setAttribute("aria-activedescendent", tabId);
  };

  private readonly onTabKeydown = (e: KeyboardEvent) => {
    const activeIndex = this._tabs.indexOf(e.currentTarget as HTMLElement);
    switch (e.key) {
      case Keys.Home:
        this._tabs[0].focus();
        break;
      case Keys.End:
        this._tabs[this._tabs.length - 1].focus();
        break;
      case Keys.ArrowUp:
      case Keys.ArrowLeft:
        if (activeIndex > 0) {
          this._tabs[activeIndex - 1].focus();
        } else {
          this._tabs[this._tabs.length - 1].focus();
        }
        break;
      case Keys.ArrowDown:
      case Keys.ArrowRight:
        if (activeIndex === this._tabs.length - 1) {
          this._tabs[0].focus();
        } else {
          this._tabs[activeIndex + 1].focus();
        }
        break;
      default:
        break;
    }
  };

  private readonly getTabPanel = (tab: HTMLElement) => {
    const panelId = tab.getAttribute("aria-controls");
    if (panelId) {
      return document.getElementById(panelId);
    }
    return null;
  };

  // For widgets with device-specific tabs, it might be necessary to set a new default active tab after instantiation
  // For the sake of not messing up styling, this method does not handle showing / hiding the associated tabpanel
  public static setNewDefaultActiveTab(tab: HTMLElement) {
    tab.setAttribute("aria-selected", "true");
    tab.setAttribute("tabindex", "0");
    tab.parentElement.classList.add("active");

    const tabList = tab.closest("[role='tablist']");
    tabList?.setAttribute("aria-activedescendent", tab.id);
    const allTabs = tabList?.querySelectorAll("[role='tab']");
    allTabs.forEach((t) => {
      if (t === tab) {
        return;
      }
      t.setAttribute("aria-selected", "false");
      t.setAttribute("tabindex", "-1");
      t.parentElement.classList.remove("active");
    });
  }
}

function instantiateTabWidgets() {
  const tablists = document.querySelectorAll("[role='tablist']");

  tablists.forEach((tabList: HTMLElement) => new AccessibleTabWidget(tabList));
}

if (document.readyState === "loading") {
  window.addEventListener("DOMContentLoaded", instantiateTabWidgets);
} else {
  instantiateTabWidgets();
}
